/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CSVLink } from "react-csv";
import {
    Alert,
    Box,
    Breadcrumbs as MuiBreadcrumbs,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    Link,
    MenuItem,
    Paper as MuiPaper,
    Select,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
    Toolbar as MuiToolbar,
    Tooltip,
} from "@mui/material";
import { spacing } from "@mui/system";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import Download from "@mui/icons-material/Download";
import moment from "moment";
import _ from "lodash";
import { connect } from "react-redux";
import axios from "../../CustomAxios";
import Loading from "../components/Loading";
import swal from "sweetalert";
import styled from "styled-components/macro";

// ---------- Styled ----------
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);
const Toolbar = styled(MuiToolbar)(spacing);
const Spacer = styled.div`flex: 1 1;`;
const ToolbarTitle = styled.div`min-width: 150px;`;

const getApiBase = (tokenType) => `/api/v1/${tokenType}`;

const normalizeAmount = (value) => {
    if (value === null || value === undefined || value === "") return "";
    if (typeof value === "object" && value.$numberDecimal !== undefined)
        return value.$numberDecimal;
    return String(value);
};

// ---------- Inline Editable Table ----------
function FixedCostEditableTable({
    rows,
    columns,
    total,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    selectedSheetId,
    onCreateRow,
    onUpdateRow,
    onDeleteRow,
    onAddColumn,
    onDeleteColumn,
    addPermission,
    editPermission,
    deletePermission,
}) {
    const [editingRowId, setEditingRowId] = useState(null);
    const [draft, setDraft] = useState({ date: "", amounts: {}, notes: "" });
    const [newRowMode, setNewRowMode] = useState(false);
    const [saving, setSaving] = useState(false);

    // column dialog
    const [colDialogOpen, setColDialogOpen] = useState(false);
    const [newColLabel, setNewColLabel] = useState("");

    const visibleColumns = useMemo(
        () =>
            (columns || [])
                .filter((c) => !c.is_deleted)
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
        [columns]
    );

    // Normalize amounts from Decimal128 to plain numbers
    const normalizeAmounts = (amountsObj) => {
        if (!amountsObj) return {};
        const clean = {};
        for (const [key, val] of Object.entries(amountsObj)) {
            if (val === null || val === undefined || val === "") {
                clean[key] = "";
                continue;
            }
            if (typeof val === "object" && val.$numberDecimal !== undefined) {
                clean[key] = Number(val.$numberDecimal);
            } else if (typeof val === "string" && !isNaN(Number(val))) {
                clean[key] = Number(val);
            } else {
                clean[key] = val;
            }
        }
        return clean;
    };

    // Lock editing if another edit is active
    const isLocked = editingRowId !== null || newRowMode;

    const startNewRow = () => {
        if (isLocked) {
            swal("", "Please save current changes first", "warning");
            return;
        }
        setDraft({ date: "", amounts: {}, notes: "" });
        setNewRowMode(true);
    };

    const startEditRow = (row) => {
        if (isLocked) {
            swal("", "Please save current changes first", "warning");
            return;
        }
        setEditingRowId(row.id);
        setDraft({
            date: row.date ? moment(row.date).format("YYYY-MM-DD") : "",
            amounts: normalizeAmounts(row.amounts),
            notes: row.notes || "",
        });
    };

    const cancelEdit = () => {
        setEditingRowId(null);
        setDraft({ date: "", amounts: {}, notes: "" });
        setNewRowMode(false);
    };

    const handleSave = async () => {
        if (!draft.date) {
            swal("", "Date is required", "error");
            return;
        }
        try {
            setSaving(true);
            // Double‑sanitize before sending
            const cleanAmounts = {};
            for (const [key, val] of Object.entries(draft.amounts)) {
                if (val === "" || val === null || val === undefined) continue;
                if (typeof val === "object" && val.$numberDecimal !== undefined) {
                    cleanAmounts[key] = Number(val.$numberDecimal);
                } else {
                    const num = Number(val);
                    cleanAmounts[key] = isNaN(num) ? val : num;
                }
            }
            const payload = { date: draft.date, notes: draft.notes, amounts: cleanAmounts };
            if (newRowMode) {
                await onCreateRow(payload);
            } else if (editingRowId) {
                await onUpdateRow(editingRowId, payload);
            }
            cancelEdit();
        } catch (err) {
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteRow = (rowId) => {
        swal({
            title: "Delete this row?",
            icon: "warning",
            buttons: true,
            dangerMode: true,
        }).then((willDelete) => {
            if (willDelete) onDeleteRow(rowId);
        });
    };

    const handleAddColumn = () => {
        if (!newColLabel.trim()) return;
        onAddColumn(newColLabel.trim());
        setNewColLabel("");
        setColDialogOpen(false);
    };

    const handleDeleteColumn = (colId) => {
        swal({
            title: "Delete this column?",
            icon: "warning",
            buttons: true,
            dangerMode: true,
        }).then((willDelete) => {
            if (willDelete) onDeleteColumn(colId);
        });
    };

    const canAdd = addPermission && selectedSheetId;
    const canEdit = editPermission;
    const canDelete = deletePermission;

    return (
        <Paper>
            {/* Top bar: Add Row + Add Column */}
            <Box sx={{ display: "flex", justifyContent: "space-between", p: 2 }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                    {canAdd && (
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={startNewRow}
                            disabled={isLocked || saving}
                        >
                            Add Row
                        </Button>
                    )}
                    {canEdit && (
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => setColDialogOpen(true)}
                        >
                            Add Column
                        </Button>
                    )}
                </Box>
            </Box>

            <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sx={{
                                    position: "sticky",
                                    left: 0,
                                    backgroundColor: "#fff",
                                    zIndex: 2,
                                }}
                            >
                                Date
                            </TableCell>
                            {visibleColumns.map((col) => (
                                <TableCell key={col._id} align="left">
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        {col.label}
                                        {canDelete && (
                                            <Tooltip title="Delete column">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDeleteColumn(col._id)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </TableCell>
                            ))}
                            {(canEdit || canDelete) && (
                                <TableCell align="center">Actions</TableCell>
                            )}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {/* New row draft */}
                        {newRowMode && (
                            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                                <TableCell
                                    sx={{
                                        position: "sticky",
                                        left: 0,
                                        backgroundColor: "#f5f5f5",
                                        zIndex: 1,
                                    }}
                                >
                                    <TextField
                                        type="date"
                                        size="small"
                                        value={draft.date}
                                        onChange={(e) =>
                                            setDraft((d) => ({ ...d, date: e.target.value }))
                                        }
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        autoFocus
                                    />
                                </TableCell>
                                {visibleColumns.map((col) => (
                                    <TableCell key={col._id}>
                                        <TextField
                                            size="small"
                                            type="number"
                                            inputProps={{ step: "any" }}
                                            value={draft.amounts?.[col._id] ?? ""}
                                            onChange={(e) =>
                                                setDraft((d) => ({
                                                    ...d,
                                                    amounts: { ...d.amounts, [col._id]: e.target.value },
                                                }))
                                            }
                                            fullWidth
                                        />
                                    </TableCell>
                                ))}
                                <TableCell align="center">
                                    <IconButton color="primary" onClick={handleSave} disabled={saving}>
                                        {saving ? <CircularProgress size={20} /> : <SaveIcon />}
                                    </IconButton>
                                    <IconButton color="inherit" onClick={cancelEdit} disabled={saving}>
                                        <CloseIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        )}

                        {/* Existing rows */}
                        {rows.map((row) => {
                            const isEditing = editingRowId === row.id;
                            return (
                                <TableRow key={row.id} hover>
                                    <TableCell
                                        sx={{
                                            position: "sticky",
                                            left: 0,
                                            backgroundColor: "#fff",
                                            zIndex: 1,
                                        }}
                                    >
                                        {isEditing ? (
                                            <TextField
                                                type="date"
                                                size="small"
                                                value={draft.date}
                                                onChange={(e) =>
                                                    setDraft((d) => ({ ...d, date: e.target.value }))
                                                }
                                                InputLabelProps={{ shrink: true }}
                                                fullWidth
                                            />
                                        ) : (
                                            moment(row.date).format("DD/MM/YYYY")
                                        )}
                                    </TableCell>

                                    {visibleColumns.map((col) => (
                                        <TableCell key={col._id}>
                                            {isEditing ? (
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    inputProps={{ step: "any" }}
                                                    value={draft.amounts?.[col._id] ?? ""}
                                                    onChange={(e) =>
                                                        setDraft((d) => ({
                                                            ...d,
                                                            amounts: { ...d.amounts, [col._id]: e.target.value },
                                                        }))
                                                    }
                                                    fullWidth
                                                />
                                            ) : (
                                                normalizeAmount(row.amounts?.[col._id])
                                            )}
                                        </TableCell>
                                    ))}

                                    {(canEdit || canDelete) && (
                                        <TableCell align="center">
                                            {isEditing ? (
                                                <>
                                                    <IconButton color="primary" onClick={handleSave} disabled={saving}>
                                                        {saving ? <CircularProgress size={20} /> : <SaveIcon />}
                                                    </IconButton>
                                                    <IconButton color="inherit" onClick={cancelEdit} disabled={saving}>
                                                        <CloseIcon />
                                                    </IconButton>
                                                </>
                                            ) : (
                                                <>
                                                    {canEdit && (
                                                        <Tooltip title="Edit row">
                                                            <IconButton onClick={() => startEditRow(row)}>
                                                                <EditIcon color="error" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {canDelete && (
                                                        <Tooltip title="Delete row">
                                                            <IconButton onClick={() => handleDeleteRow(row.id)}>
                                                                <DeleteIcon color="warning" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
            />

            {/* Add Column Dialog */}
            <Dialog open={colDialogOpen} onClose={() => setColDialogOpen(false)}>
                <DialogTitle>Add Column</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Column Name"
                        fullWidth
                        value={newColLabel}
                        onChange={(e) => setNewColLabel(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setColDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleAddColumn} variant="contained">
                        Add
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}

// ---------- Main Container ----------
function FixedCostList({ auths }) {
    const token = JSON.parse(localStorage.getItem("AuthData"));
    const tokenType = token?.type;
    const accessToken = token?.access_token;

    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    // --- Page‑level loader (first load only) ---
    const [pageLoading, setPageLoading] = useState(true);

    // --- Table‑level loader (row refreshes) ---
    const [tableLoading, setTableLoading] = useState(false);

    // --- Notification ---
    const [message, setMessage] = useState(location?.state?.message);
    const snackRef = useRef(null);
    const [snackState, setSnackState] = useState({
        open: false,
        vertical: "top",
        horizontal: "center",
    });

    // --- Dropdowns ---
    const [apCompanies, setApCompanies] = useState([]);
    const [selectedApCompany, setSelectedApCompany] = useState("");
    const [sheets, setSheets] = useState([]);
    const [selectedSheetId, setSelectedSheetId] = useState("");

    // --- Table data ---
    const [rows, setRows] = useState([]);
    const [columns, setColumns] = useState([]);
    const [total, setTotal] = useState(0);

    // --- Permissions ---
    const [addPermission, setAddPermission] = useState(false);
    const [editPermission, setEditPermission] = useState(false);
    const [deletePermission, setDeletePermission] = useState(false);

    // --- Pagination from URL ---
    const initialPage = Number(searchParams.get("page") || 0);
    const initialRowsPerPage = Number(searchParams.get("limit") || 25);
    const [page, setPage] = useState(initialPage);
    const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

    // --- Inline sheet creation ---
    const [showNewSheetForm, setShowNewSheetForm] = useState(false);
    const [newSheetName, setNewSheetName] = useState("");

    const updateUrlParams = (newPage, newLimit) =>
        setSearchParams({ page: newPage, limit: newLimit });

    // Success snackbar
    useEffect(() => {
        if (location?.state?.success) {
            snackRef.current?.click();
            navigate(location.pathname, { replace: true });
        }
    }, [location?.state]);

    const handleSnackClose = () => setSnackState((s) => ({ ...s, open: false }));

    // Permissions
    useEffect(() => {
        if (tokenType === "super_admin") {
            setAddPermission(true);
            setEditPermission(true);
            setDeletePermission(true);
            return;
        }
        if (auths?.user?.role?.permissions) {
            const perms = auths.user.role.permissions;
            const missing = _.difference(
                ["view_fixed_cost_sheet_list", "view_fixed_cost_row_list"],
                perms
            );
            if (missing.length > 0) {
                swal("", `Missing permissions: ${missing.join(", ")}`, "error");
            }
            setAddPermission(
                perms.includes("create_fixed_cost_sheet") ||
                perms.includes("create_fixed_cost_row")
            );
            setEditPermission(perms.includes("update_fixed_cost_row"));
            setDeletePermission(perms.includes("delete_fixed_cost_row"));
        }
    }, [auths, tokenType]);

    // ---------- Fetch Companies (initial) ----------
    useEffect(() => {
        (async () => {
            try {
                setPageLoading(true);
                const { data } = await axios.get(`/api/v1/${tokenType}/ap_company`, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
                if (data.success) {
                    const list = data.data?.data || [];
                    setApCompanies(list);
                    if (list.length > 0) setSelectedApCompany(list[0]._id);
                }
            } catch (e) {
                console.error(e);
            } finally {
                setPageLoading(false);
            }
        })();
    }, []);

    // ---------- Fetch Sheets ----------
    const fetchSheets = useCallback(async () => {
        if (!selectedApCompany) return [];
        try {
            const url = `${getApiBase(tokenType)}/${selectedApCompany}/fixed_cost_sheets`;
            const { data } = await axios.get(url, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (data.success) {
                const list = data.data?.data || [];
                setSheets(list);
                return list;
            }
        } catch (e) {
            console.error(e);
        }
        return [];
    }, [selectedApCompany, tokenType, accessToken]);

    // When company changes, reset sheets and select the first one
    useEffect(() => {
        if (!selectedApCompany) {
            setSheets([]);
            setSelectedSheetId("");
            setRows([]);
            setColumns([]);
            setTotal(0);
            return;
        }
        fetchSheets().then((list) => {
            if (list.length > 0) {
                setSelectedSheetId((prev) =>
                    list.some((s) => s._id === prev) ? prev : list[0]._id
                );
            } else {
                setSelectedSheetId("");
            }
        });
    }, [selectedApCompany, fetchSheets]);

    // ---------- Derive columns from selected sheet ----------
    useEffect(() => {
        const activeSheet = sheets.find((s) => s._id === selectedSheetId);
        setColumns(activeSheet?.columns || []);
    }, [selectedSheetId, sheets]);

    // ---------- Fetch Rows ----------
    const fetchRows = useCallback(async () => {
        if (!selectedApCompany || !selectedSheetId) return;
        try {
            setTableLoading(true);
            const url = `${getApiBase(tokenType)}/${selectedApCompany}/fixed_cost_sheets/${selectedSheetId}/rows?page=${page + 1
                }&limit=${rowsPerPage}`;
            const { data } = await axios.get(url, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (data.success) {
                const results = data.data?.data || [];
                const meta = data.data?.metadata || {};
                setTotal(meta.total_results || 0);
                setRows(
                    results.map((item) => ({
                        id: item._id,
                        date: item.date,
                        amounts: item.amounts || {},
                        notes: item.notes,
                    }))
                );
            }
        } catch (e) {
            console.error(e);
        } finally {
            setTableLoading(false);
        }
    }, [selectedApCompany, selectedSheetId, page, rowsPerPage, tokenType, accessToken]);

    // Fetch rows when needed
    useEffect(() => {
        if (selectedSheetId) fetchRows();
    }, [fetchRows, selectedSheetId]);

    // ---------- Create Sheet (inline) ----------
    const handleCreateSheet = async () => {
        if (!newSheetName.trim()) return;
        try {
            const url = `${getApiBase(tokenType)}/${selectedApCompany}/fixed_cost_sheets`;
            const { data } = await axios.post(
                url,
                { name: newSheetName, columns: [] },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            if (data.success) {
                await fetchSheets();
                setSelectedSheetId(data.data._id);
                setShowNewSheetForm(false);
                setNewSheetName("");
            }
        } catch (err) {
            console.error(err);
        }
    };

    // ---------- Row CRUD ----------
    const onCreateRow = async (payload) => {
        await axios.post(
            `${getApiBase(tokenType)}/${selectedApCompany}/fixed_cost_sheets/${selectedSheetId}/rows`,
            payload,
            { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        fetchRows();
    };

    const onUpdateRow = async (rowId, payload) => {
        await axios.put(
            `${getApiBase(tokenType)}/${selectedApCompany}/fixed_cost_sheets/${selectedSheetId}/rows/${rowId}`,
            payload,
            { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        fetchRows();
    };

    const onDeleteRow = async (rowId) => {
        await axios.delete(
            `${getApiBase(tokenType)}/${selectedApCompany}/fixed_cost_sheets/${selectedSheetId}/rows/${rowId}`,
            { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        fetchRows();
    };

    // ---------- Column Management (dedicated endpoints) ----------
    const onAddColumn = async (label) => {
        const { data } = await axios.post(
            `${getApiBase(tokenType)}/${selectedApCompany}/fixed_cost_sheets/${selectedSheetId}/columns`,
            { label },
            { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (data.success) {
            setColumns(data.data.columns);
            fetchSheets();
        }
    };

    const onDeleteColumn = async (colId) => {
        const { data } = await axios.put(
            `${getApiBase(tokenType)}/${selectedApCompany}/fixed_cost_sheets/${selectedSheetId}/columns/${colId}/soft-delete`,
            {},
            { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (data.success) {
            setColumns(data.data.columns);
            fetchSheets();
        }
    };

    // ---------- CSV download data (using col._id) ----------
    const visibleColumns = useMemo(
        () =>
            (columns || [])
                .filter((c) => !c.is_deleted)
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
        [columns]
    );

    const csvHeaders = useMemo(
        () => [
            { label: "Date", key: "date" },
            ...visibleColumns.map((col) => ({
                label: col.label,
                key: col._id,               // ✅ correct key
            })),
        ],
        [visibleColumns]
    );

    const csvData = useMemo(() => {
        return rows.map((row) => {
            const obj = {
                date: row.date ? moment(row.date).format("YYYY-MM-DD") : "",
            };
            visibleColumns.forEach((col) => {
                const key = col._id;        // ✅ correct key
                let value = row.amounts?.[key];
                // Normalise Decimal128 values
                if (value && typeof value === "object" && value.$numberDecimal !== undefined) {
                    value = value.$numberDecimal;
                }
                obj[key] = value ?? "";
            });
            return obj;
        });
    }, [rows, visibleColumns]);

    // ✅ CSV filename based on selected sheet name
    const csvFilename = useMemo(() => {
        const sheet = sheets.find(s => s._id === selectedSheetId);
        const rawName = sheet?.name || 'FixedCosts';
        // Remove characters not allowed in filenames (slashes, colons, etc.)
        const safeName = rawName.replace(/[^a-zA-Z0-9 _-]/g, '').trim() || 'FixedCosts';
        return `${safeName}_${moment().format('DD-MM-YY_HH-mm')}.csv`;
    }, [sheets, selectedSheetId]);

    // ---------- Pagination handlers ----------
    const handleChangePage = (_, newPage) => {
        setPage(newPage);
        updateUrlParams(newPage, rowsPerPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const newLimit = parseInt(event.target.value, 10);
        setRowsPerPage(newLimit);
        setPage(0);
        updateUrlParams(0, newLimit);
    };

    // ---------- Render ----------
    if (pageLoading) return <Loading />;

    return (
        <React.Fragment>
            <Helmet title="Fixed Costs" />
            {/* Header */}
            <Grid container justifyContent="space-between" alignItems="center" mb={4}>
                <Grid item>
                    <Typography variant="h4" fontWeight={600} color="primary">
                        FIXED COSTS
                    </Typography>
                </Grid>
                <Grid item>
                    <Breadcrumbs>
                        <Link component={NavLink} to="/dashboard/tasks">Dashboard</Link>
                        <Typography>Fixed Costs</Typography>
                    </Breadcrumbs>
                </Grid>
            </Grid>

            {/* Success snackbar */}
            <Button ref={snackRef} sx={{ display: "none" }} onClick={() =>
                setSnackState({ open: true, vertical: "top", horizontal: "center" })
            } />
            <Snackbar
                anchorOrigin={{
                    vertical: snackState.vertical,
                    horizontal: snackState.horizontal,
                }}
                open={snackState.open}
                autoHideDuration={3000}
                onClose={handleSnackClose}
            >
                <Alert severity="success">{message}</Alert>
            </Snackbar>

            {/* Company & Sheet selector + CSV download */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Toolbar sx={{ gap: 2, flexWrap: "wrap" }}>
                    <ToolbarTitle>
                        <Typography variant="h6">Fixed Costs</Typography>
                    </ToolbarTitle>
                    <Spacer />
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>AP Company</InputLabel>
                        <Select
                            label="AP Company"
                            value={selectedApCompany}
                            onChange={(e) => setSelectedApCompany(e.target.value)}
                        >
                            <MenuItem value="" disabled>Select a company</MenuItem>
                            {apCompanies.map((c) => (
                                <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 240 }} disabled={!selectedApCompany}>
                        <InputLabel>Spreadsheet</InputLabel>
                        <Select
                            label="Spreadsheet"
                            value={selectedSheetId}
                            onChange={(e) => setSelectedSheetId(e.target.value)}
                        >
                            <MenuItem value="" disabled>Select a spreadsheet</MenuItem>
                            {sheets.map((s) => (
                                <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* CSV Download Button (shows only when data exists) */}
                    {csvData && csvData.length > 0 && (
                        <CSVLink
                            data={csvData}
                            headers={csvHeaders}
                            filename={csvFilename}
                            style={{ textDecoration: 'none' }}
                        >
                            <Button variant="outlined" endIcon={<Download />}>
                                CSV
                            </Button>
                        </CSVLink>
                    )}
                </Toolbar>
            </Paper>

            {/* Content area */}
            <Box mt={3}>
                {!selectedApCompany ? (
                    <Box sx={{ height: "60vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <Typography variant="h5">Select a company</Typography>
                    </Box>
                ) : !selectedSheetId ? (
                    <Box
                        sx={{
                            height: "60vh",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 2,
                        }}
                    >
                        <Typography variant="h5">No spreadsheet yet for this company</Typography>
                        {addPermission &&
                            (!showNewSheetForm ? (
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setShowNewSheetForm(true)}
                                >
                                    Add Spreadsheet
                                </Button>
                            ) : (
                                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                    <TextField
                                        size="small"
                                        label="Sheet Name"
                                        value={newSheetName}
                                        onChange={(e) => setNewSheetName(e.target.value)}
                                        autoFocus
                                    />
                                    <Button variant="contained" onClick={handleCreateSheet}>
                                        Create
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            setShowNewSheetForm(false);
                                            setNewSheetName("");
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            ))}
                    </Box>
                ) : (
                    <Box sx={{ position: "relative" }}>
                        {tableLoading && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: "rgba(255,255,255,0.7)",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    zIndex: 10,
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        )}
                        <FixedCostEditableTable
                            rows={rows}
                            columns={columns}
                            total={total}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            selectedSheetId={selectedSheetId}
                            onCreateRow={onCreateRow}
                            onUpdateRow={onUpdateRow}
                            onDeleteRow={onDeleteRow}
                            onAddColumn={onAddColumn}
                            onDeleteColumn={onDeleteColumn}
                            addPermission={addPermission}
                            editPermission={editPermission}
                            deletePermission={deletePermission}
                        />
                    </Box>
                )}
            </Box>
        </React.Fragment>
    );
}

const mapStateToProps = (state) => ({ auths: state.auths });
export default connect(mapStateToProps, null)(FixedCostList);